import { getAccountTypeIcon } from 'components/app-components/withdrawals/PayoutWithdrawalMethodItem';
import { Column } from 'components/generic-components/generic-table/type';
import CopyableText from 'components/generic-components/copyableText';
import StatusPill from 'components/app-components/ui/status-pill';
import ArrowBackInvoiceModal from 'icons/ArrowBackInvoiceModal';
import BankTransferIcon from 'icons/BankTransferIcon';
import InvoicePaymentIcon from 'icons/InvoicePaymentIcon';
import TheraAdvanceIcon from 'icons/TheraAdvanceIcon';
import Wallet from 'icons/Wallet';
import { useMemo } from 'react';
import useIntl from 'hooks/useIntl';
import { useUser } from 'context/user';
import { WithdrawalAccountType } from 'lib/constants';
import { UnifiedTransaction, UNIFIED_TYPE_LABELS, formatTransactionDateTime } from 'utils/unifiedTransactions';
import { showIDToCopy } from 'utils/user';

export const useUnifiedColumns = (): Column<UnifiedTransaction>[] => {
  const { formatCurrencyToLocale } = useIntl();
  const { user } = useUser();
  return useMemo(
    () => [
      {
        header: 'TRANSACTION TYPE',
        id: 'type',
        accessorKey: 'type',
        filterFn: 'arrIncludesSome',
        cell: ({ row }) => {
          const { type, isCredit, withdrawalMethodAccountType, ledgerTransactionId, withdrawalMethodId } = row.original;
          const arrowClass = `h-3 w-3 transform ${isCredit ? '-rotate-45' : 'rotate-135'}`;

          const renderIcon = () => {
            switch (type) {
              case 'thera_advance_withdrawal':
                return (
                  <>
                    <span className="flex h-10 w-10 min-w-10 items-center justify-center rounded-full border border-gray-200 bg-white">
                      <TheraAdvanceIcon className="text-neutral-700" />
                    </span>
                    <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-neutral-100">
                      <ArrowBackInvoiceModal className="h-3 w-3 rotate-135 transform" />
                    </span>
                  </>
                );
              case 'thera_advance_repayment':
                return (
                  <>
                    <span className="flex h-10 w-10 min-w-10 items-center justify-center rounded-full border border-gray-200 bg-white">
                      <TheraAdvanceIcon className="text-neutral-700" />
                    </span>
                    <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-neutral-100">
                      <ArrowBackInvoiceModal className="h-3 w-3 -rotate-45 transform" />
                    </span>
                  </>
                );
              case 'invoice_payment':
                return (
                  <>
                    <span className="flex h-10 w-10 min-w-10 items-center justify-center rounded-full border border-gray-200 bg-white">
                      <InvoicePaymentIcon className="text-neutral-700" />
                    </span>
                    <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-neutral-100">
                      <ArrowBackInvoiceModal className="h-3 w-3 -rotate-45 transform" />
                    </span>
                  </>
                );
              case 'withdrawals':
                return (
                  <>
                    <span className="flex h-10 w-10 min-w-10 items-center justify-center rounded-full border border-gray-200 bg-white">
                      {getAccountTypeIcon(withdrawalMethodAccountType as WithdrawalAccountType)}
                    </span>
                    <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-neutral-100">
                      <ArrowBackInvoiceModal className={arrowClass} />
                    </span>
                  </>
                );
              case 'wallet_credit':
                return (
                  <>
                    <span className="flex h-10 w-10 min-w-10 items-center justify-center rounded-full border border-gray-200 bg-white">
                      <Wallet />
                    </span>
                    <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-neutral-100">
                      <ArrowBackInvoiceModal className={arrowClass} />
                    </span>
                  </>
                );
              case 'wallet_debit':
                return (
                  <>
                    <span className="flex h-10 w-10 min-w-10 items-center justify-center rounded-full border border-gray-200 bg-white">
                      <BankTransferIcon />
                    </span>
                    <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-neutral-100">
                      <ArrowBackInvoiceModal className={arrowClass} />
                    </span>
                  </>
                );
              default:
                return (
                  <span className="flex h-10 w-10 min-w-10 items-center justify-center rounded-full border border-gray-200 bg-white">
                    <ArrowBackInvoiceModal className={arrowClass} />
                  </span>
                );
            }
          };

          const showIDs = showIDToCopy(user);

          return (
            <div className="flex items-center">
              <div className="relative mr-3">{renderIcon()}</div>
              <div className="flex flex-col">
                <p className="text-sm font-normal text-neutral-900">{UNIFIED_TYPE_LABELS[type]}</p>
                {showIDs && ledgerTransactionId && (
                  <CopyableText text={ledgerTransactionId} containerClassName="mt-1" label="Ledger TxnId" />
                )}
                {showIDs && withdrawalMethodId && (
                  <CopyableText text={withdrawalMethodId} containerClassName="mt-1" label="Withdrawal MethodId" />
                )}
              </div>
            </div>
          );
        },
        accessorFn: ({ type }) => UNIFIED_TYPE_LABELS[type],
        meta: { className: '!text-sm !font-normal !text-neutral-900' },
      },
      {
        header: 'DATE',
        id: 'date',
        accessorKey: 'date',
        filterFn: 'arrIncludesSome',
        cell: () => '',
        meta: {
          className: '!text-sm !font-normal !text-neutral-900',
          icon: (row: UnifiedTransaction) => {
            const { date, time } = formatTransactionDateTime(row.date);
            return (
              <p>
                {date}
                <span className="ml-2 text-xs text-neutral-500">{time}</span>
              </p>
            );
          },
        },
        accessorFn: (row) => formatTransactionDateTime(row.date).date,
      },
      {
        header: 'PAYMENT STATUS',
        id: 'status',
        accessorKey: 'status',
        filterFn: 'arrIncludesSome',
        cell: () => '',
        meta: {
          className: '!text-sm !font-normal !text-neutral-900',
          icon: ({ status, pillStatus }: UnifiedTransaction) => <StatusPill text={status} status={pillStatus} />,
        },
        accessorFn: ({ status }) => status,
      },
      {
        header: 'AMOUNT',
        id: 'amount',
        accessorKey: 'amount',
        filterFn: 'arrIncludesSome',
        hideJustFromFilterList: true,
        cell: () => '',
        meta: {
          className: '!text-sm !font-normal !text-neutral-900',
          icon: ({ amount, currency, isCredit, convertedAmount, convertedCurrency }: UnifiedTransaction) => {
            const absAmount = Math.abs(amount);
            const isCrypto = ['usdc', 'usdt', 'usdb', 'eurc'].includes(currency.toLowerCase());
            const formattedAmount = isCrypto
              ? `${absAmount / 100} ${currency.toUpperCase()}`
              : formatCurrencyToLocale(absAmount, currency);
            return (
              <div className="font-medium">
                <p data-sensitive-copy-admins>
                  <span className="mr-1">{isCredit ? '+' : '-'}</span>
                  {formattedAmount}
                </p>
                {convertedAmount && convertedCurrency && convertedCurrency.toUpperCase() !== currency.toUpperCase() && (
                  <p className="mt-1.5 text-xs text-neutral-500">
                    to {formatCurrencyToLocale(convertedAmount, convertedCurrency)}
                  </p>
                )}
              </div>
            );
          },
        },
        accessorFn: ({ amount }) => Math.abs(amount) / 100,
      },
    ],
    [formatCurrencyToLocale, user],
  );
};
